import { ref, onMounted, onBeforeUnmount } from 'vue';

/** Whether the background code screen is in sight, shared by every component: it only animates meanwhile. */
export const codeScreenInSight = ref(true);

/** Opacity of the background code screen, shared by every component. */
export const codeScreenOpacity = ref(1);

/**
 * Tracks `codeScreenInSight` with the `el` template ref, the top section of the page that the
 * code screen shows through. It goes out of sight once the next section has scrolled over all
 * but the top quarter of the viewport, where the code is dim, and stays out of sight down the
 * rest of the page.
 */
export function useCodeScreenSight(el) {
    let observer = null;

    onMounted(() => {
        // The root shrinks to the viewport below its top quarter.
        observer = new IntersectionObserver(
            (entries) => {
                codeScreenInSight.value = entries[entries.length - 1].isIntersecting;
            },
            { rootMargin: '-25% 0px 0px 0px' },
        );
        observer.observe(el.value);
    });
    onBeforeUnmount(() => observer.disconnect());
}

/**
 * Tracks `codeScreenOpacity` with the `el` template ref, the section from which the code screen
 * no longer shows through the page: it fades out as that section scrolls up the viewport, and
 * stays transparent once the section reaches the top, down the rest of the page.
 */
export function useCodeScreenFade(el) {
    const update = () => {
        const top = el.value.getBoundingClientRect().top / window.innerHeight;
        codeScreenOpacity.value = Math.min(1, Math.max(0, top));
    };

    onMounted(() => {
        update();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
    });
    onBeforeUnmount(() => {
        window.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
    });
}
